import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Activity,
  CheckCircle,
  XCircle,
  RefreshCw,
  Zap,
  Database,
  Server,
  Clock,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  BarChart3,
} from 'lucide-react';
import { Card, Loading, Badge } from '../../components/common';
import adminService from '../../services/api/admin.service';

// 🔥 Função para detectar status real baseado no context
const getConversationStatus = (conversation) => {
  let currentStep = '';
  
  if (conversation.context) {
    currentStep = conversation.context.current_step || 
                 conversation.context.currentStep || 
                 conversation.context.step ||
                 conversation.context.stepId ||
                 conversation.context.step_id || '';
  }
  
  if (!currentStep && conversation) {
    currentStep = conversation.current_step ||
                 conversation.currentStep ||
                 conversation.step || '';
  }
  
  const stepStr = String(currentStep).toLowerCase();
  
  if (stepStr.includes('perguntar_reinicio')) {
    return 'closed';
  }
  
  if (conversation.context?.flow_finished === true || 
      conversation.context?.flow_finished === 'true') {
    return 'closed';
  }
  
  if (conversation.context?.restarted_by_user === true || 
      conversation.context?.restarted_by_user === 'true') {
    return 'open';
  }
  
  return conversation.status || 'open';
};

const AdminOverview = () => {
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const healthData = await adminService.getHealth();
      setHealth(healthData);

      const metricsData = await adminService.calculateMetrics();
      setMetrics(metricsData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" text="Carregando dashboard..." />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2.5 font-display">
            <div className="w-8 h-8 bg-primary-50 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
              <LayoutDashboard className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            </div>
            Dashboard Administrativo
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 ml-10">
            Monitoramento em tempo real da plataforma
          </p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 self-start sm:self-auto"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {health && (
        <Card>
          <Card.Body>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${
                  health.status === 'ok'
                    ? 'bg-emerald-50 dark:bg-emerald-900/20'
                    : 'bg-amber-50 dark:bg-amber-900/20'
                }`}>
                  <Activity className={`w-5 h-5 ${
                    health.status === 'ok'
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-amber-600 dark:text-amber-400'
                  }`} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Status do Sistema</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {health.status === 'ok' ? 'Todos os sistemas operacionais' : 'Alguns serviços degradados'}
                  </p>
                </div>
              </div>
              <Badge variant={health.status === 'ok' ? 'success' : 'warning'} dot>
                {health.status === 'ok' ? 'Operacional' : 'Degradado'}
              </Badge>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <ServiceStatus icon={Server}       name="API"       description="Aplicação principal"  status={health.services.api} />
              <ServiceStatus icon={Database}     name="Database"  description="PostgreSQL"            status={health.services.database} />
              <ServiceStatus icon={Activity}     name="Redis"     description="Cache & Queue"         status={health.services.redis} />
              <ServiceStatus icon={MessageSquare} name="Evolution" description="WhatsApp Provider"    status={health.services.evolution} />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400 dark:text-slate-500">
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                <span>Uptime: {Math.floor(health.uptime / 60)} min</span>
              </div>
              <span>·</span>
              <span>Atualizado: {new Date(health.timestamp).toLocaleTimeString('pt-BR')}</span>
            </div>
          </Card.Body>
        </Card>
      )}

      {metrics && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={Users}        iconColor="blue"   title="Total de Tenants"
              value={metrics.totalTenants}
              subtitle={`${metrics.activeTenants} ativos, ${metrics.inactiveTenants} inativos`}
              trend={metrics.activeTenants > metrics.inactiveTenants ? { type: 'up', value: 'Maioria ativa' } : null}
            />
            <MetricCard
              icon={MessageSquare} iconColor="purple" title="Conversas Totais"
              value={metrics.totalConversations}
              subtitle={`${metrics.openConversations} abertas, ${metrics.closedConversations} fechadas`}
            />
            <MetricCard
              icon={Zap}          iconColor="green"  title="Bots Ativos"
              value={metrics.botsActive}
              subtitle={`${metrics.botsPaused} pausados`}
              trend={metrics.botsActive > metrics.botsPaused ? { type: 'up', value: 'Maioria ativa' } : { type: 'down', value: 'Atenção necessária' }}
            />
            <MetricCard
              icon={TrendingUp}   iconColor="yellow" title="Taxa de Resolução"
              value={`${metrics.resolutionRate}%`}
              subtitle={`${metrics.closedConversations} resolvidas`}
              trend={parseFloat(metrics.resolutionRate) > 70 ? { type: 'up', value: 'Excelente' } : { type: 'down', value: 'Pode melhorar' }}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                title: 'Envio de Mensagens', icon: MessageSquare,
                rows: [
                  { label: 'Habilitado',  value: metrics.sendEnabled,  color: 'text-emerald-600 dark:text-emerald-400' },
                  { label: 'Bloqueado',   value: metrics.sendDisabled, color: 'text-red-600 dark:text-red-400' },
                ],
              },
              {
                title: 'Coexistência', icon: Users,
                rows: [
                  { label: 'Com agentes', value: metrics.coexEnabled,    color: 'text-primary-600 dark:text-primary-400' },
                  { label: 'Bot pausado', value: metrics.botPausedCount, color: 'text-amber-600 dark:text-amber-400' },
                ],
              },
              {
                title: 'Status Geral', icon: Activity,
                rows: [
                  { label: 'Operacionais',        value: metrics.botsActive,                     color: 'text-emerald-600 dark:text-emerald-400' },
                  { label: 'Necessitam atenção',  value: metrics.botsPaused + metrics.sendDisabled, color: 'text-amber-600 dark:text-amber-400' },
                ],
              },
            ].map(({ title, icon: Icon, rows }) => (
              <Card key={title}>
                <Card.Body>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{title}</h3>
                    <Icon className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                  </div>
                  <div className="space-y-2.5">
                    {rows.map(({ label, value, color }) => (
                      <div key={label} className="flex items-center justify-between">
                        <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
                        <span className={`text-lg font-bold font-display ${color}`}>{value}</span>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>

          <Card>
            <Card.Header>
              <div className="flex items-center justify-between">
                <div>
                  <Card.Title>Conversas por Tenant</Card.Title>
                  <Card.Description>Distribuição de conversas entre os tenants</Card.Description>
                </div>
                <BarChart3 className="w-4 h-4 text-slate-400 dark:text-slate-500" />
              </div>
            </Card.Header>
            <Card.Body>
              <div className="space-y-2">
                {metrics.conversationsByTenant.length === 0 ? (
                  <div className="text-center py-8 text-slate-400 dark:text-slate-500 text-sm">
                    Nenhum tenant com conversas
                  </div>
                ) : (
                  metrics.conversationsByTenant
                    .sort((a, b) => b.total - a.total)
                    .map((item) => (
                      <div
                        key={item.tenantId}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3.5 bg-slate-50 dark:bg-slate-900/50 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                            {item.tenantName}
                          </h3>
                          <p className="text-xs text-slate-400 dark:text-slate-500 font-mono truncate">{item.tenantId}</p>
                        </div>

                        {/* Stats — flex-wrap so they never overflow */}
                        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 sm:ml-4 flex-shrink-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xl font-bold text-slate-900 dark:text-slate-100 font-display">{item.total}</span>
                            <span className="text-xs text-slate-400 dark:text-slate-500">total</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-base font-semibold text-emerald-600 dark:text-emerald-400 font-display">{item.open}</span>
                            <span className="text-xs text-slate-400 dark:text-slate-500">abertas</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-base font-semibold text-slate-500 dark:text-slate-400 font-display">{item.closed}</span>
                            <span className="text-xs text-slate-400 dark:text-slate-500">fechadas</span>
                          </div>
                          {item.botPaused > 0 && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-base font-semibold text-amber-600 dark:text-amber-400 font-display">{item.botPaused}</span>
                              <span className="text-xs text-slate-400 dark:text-slate-500">pausadas</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                )}
              </div>
            </Card.Body>
          </Card>

          {(metrics.sendDisabled > 0 || metrics.botsPaused > 0 || metrics.botPausedCount > 0) && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1.5">Atenção Necessária</h3>
                <ul className="space-y-1 text-xs text-amber-700 dark:text-amber-400">
                  {metrics.sendDisabled > 0  && <li>• {metrics.sendDisabled} tenant(s) com envio bloqueado</li>}
                  {metrics.botsPaused > 0    && <li>• {metrics.botsPaused} tenant(s) com bot pausado</li>}
                  {metrics.botPausedCount > 0 && <li>• {metrics.botPausedCount} conversa(s) com bot pausado (handoff)</li>}
                </ul>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ServiceStatus = ({ icon: Icon, name, description, status }) => (
  <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
    <Icon className="w-4 h-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{name}</p>
      <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{description}</p>
    </div>
    {status === 'healthy'
      ? <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
      : status === 'not_configured'
      ? <XCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />
      : <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
    }
  </div>
);

const MetricCard = ({ icon: Icon, iconColor, title, value, subtitle, trend }) => {
  const colorClasses = {
    blue:   'bg-blue-50 dark:bg-blue-900/25 text-blue-600 dark:text-blue-400',
    purple: 'bg-purple-50 dark:bg-purple-900/25 text-purple-600 dark:text-purple-400',
    green:  'bg-emerald-50 dark:bg-emerald-900/25 text-emerald-600 dark:text-emerald-400',
    yellow: 'bg-amber-50 dark:bg-amber-900/25 text-amber-600 dark:text-amber-400',
  };

  return (
    <Card hoverable>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">{title}</p>
          <p className="text-3xl font-bold text-slate-900 dark:text-slate-50 font-display leading-none mb-1">{value}</p>
          <p className="text-xs text-slate-400 dark:text-slate-500">{subtitle}</p>
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              {trend.type === 'up'
                ? <TrendingUp   className="w-3.5 h-3.5 text-emerald-500" />
                : <TrendingDown className="w-3.5 h-3.5 text-red-500" />
              }
              <span className={`text-xs font-semibold ${trend.type === 'up' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                {trend.value}
              </span>
            </div>
          )}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClasses[iconColor]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
};

export default AdminOverview;